import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import voca from 'voca';
import { PRODUCTION, generateSurfaceUrl } from '../utils/environments';
import { snakeCaseToTitleCase } from '../utils/api_utils';
import CustomLoader from '../../features/common/CustomLoader';
import { loadOrganizationDetails } from '../utils/check-auth';
import ToggleButton from './ToggleButton';
import Swal from 'sweetalert2';

export function getPublishAccess(projectDetail, projectId) {
  const projectType = projectDetail && projectDetail.projectType;
  const subscription = loadOrganizationDetails('subscription');
  let message = '';
  let disable = false;
  let link = '';
  let linkMsg = '';
  if (!subscription || subscription === 'BUILDER_FREE') {
    disable = true;
    message = 'Please upgrade your account to enable this feature.';
    link = `${process.env.REACT_APP_ORGANIZATION}accounts`;
    linkMsg = 'Upgrade';
  }
  if (projectType === 'FREE' && subscription !== 'UNLIMITED') {
    disable = true;
    message = "You don't have cloud/hosting subscription. Please subscribe to enable this feature.";
    link = `${process.env.REACT_APP_ORGANIZATION}${projectId}/cloud-hosting`;
    linkMsg = 'Subscribe';
  }
  return { disable, message, link, linkMsg };
}

const ProductionPublishContent = ({
  projectId,
  version,
  versions,
  selVersion,
  isPublishing,
  handleVersionChange,
  publishApp,
  projectDetail,
  actions,
  variant = 'table',
  onExportCode,
  panelId,
  embedded = false,
}) => {
  const [debugModeState, setDebugModeState] = useState({});
  const [cleanPublishState, setCleanPublishState] = useState({});

  let { domainName, url, environments, seoName, debugMode = [] } = projectDetail;
  const urlToOpen = !domainName || domainName === url ? '' : `https://${domainName}`;
  environments = environments ? environments.filter((env) => env.envType !== 'PREVIEW') : [];

  useEffect(() => {
    if (environments && debugMode && debugMode.length > 0) {
      const initialState = environments.reduce((acc, env) => {
        acc[env.envType] = {
          isEnable: debugMode.includes(env.envType),
          environment: env.envType,
        };
        return acc;
      }, {});
      setDebugModeState(initialState);
    }

    if (environments) {
      const cleanInit = environments.reduce((acc, env) => {
        acc[env.envType] = true;
        return acc;
      }, {});
      setCleanPublishState(cleanInit);
    }
  }, [debugMode]);

  const handleDebugModeToggle = async (envType) => {
    const currentState = debugModeState[envType] ? debugModeState[envType].isEnable : false;
    const newState = !currentState;
    const heading = `Are you sure to ${
      newState ? 'enable' : 'disable'
    } debug mode for ${voca.titleCase(envType)}?`;
    const description = newState
      ? 'It will start displaying logs in the browser console window.'
      : 'It will stop displaying logs in the browser console window.';

    const result = await Swal.fire({
      title: heading,
      text: description,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newState ? '#3085d6' : '#d33',
      cancelButtonColor: '#757f91',
      confirmButtonText: `Yes, ${newState ? 'enable' : 'disable'} it!`,
    });

    if (result.isConfirmed) {
      const newStateObject = {
        isEnable: newState,
        environment: envType,
      };

      setDebugModeState({
        ...debugModeState,
        [envType]: newStateObject,
      });
      await actions.toggleDebugMode({ projectId, version, data: newStateObject });
    }
  };

  const handleCleanToggle = (envType) => {
    setCleanPublishState({
      ...cleanPublishState,
      [envType]: !cleanPublishState[envType],
    });
  };

  const generateProjectURL = (envType, seoNameArg, link = false, isVibe = false) => {
    let surfaceUrl = generateSurfaceUrl(envType, seoNameArg, isVibe);
    if (link) return surfaceUrl;
    return (
      <>
        <i className="wb webconnect-box-arrow-in-up-right" title="Open link in new tab"></i>
        <a
          className="icon-link"
          style={{ color: '#1F2041' }}
          href={surfaceUrl}
          target="_blank"
          rel="noreferrer"
          title="Open link in new tab"
        >
          {surfaceUrl}
        </a>
      </>
    );
  };

  const access = getPublishAccess(projectDetail, projectId);

  const productionGate = (
    <div className="dc-production-gate">
      <strong>{access.message}</strong>
      <a href={access.link} className="dc-production-btn dc-production-btn-primary">
        <i className="wb webconnect-paper-plane-fill"></i> {access.linkMsg}
      </a>
    </div>
  );

  if (access.disable) {
    if (variant === 'panel' && embedded) {
      return productionGate;
    }
    if (variant === 'panel') {
      return (
        <div
          id={panelId}
          className="dc-production-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Production"
          tabIndex={-1}
        >
          <div className="dc-production-panel-heading">
            <span className="dc-production-dot" aria-hidden="true" />
            <span>Production</span>
          </div>
          {productionGate}
        </div>
      );
    }
    return (
      <div className="row pt-2 pb-2">
        <div className="col-md-12 col-sm-12 text-center">
          <strong>{access.message}</strong>
          <br />
          <a href={access.link} className="btn btn-theme-submit btn-sm">
            <i className={'wb webconnect-paper-plane-fill'}></i> {access.linkMsg}
          </a>
        </div>
      </div>
    );
  }

  const productionEnvList = (
    <div className="dc-production-env-list">
      {environments.map((env) => {
        const surfaceUrl = generateProjectURL(env.envType, seoName, true, true);
        return (
          <section key={env.uuid} className="dc-production-env-card">
            <h3 className="dc-production-env-title">{snakeCaseToTitleCase(env.envType)}</h3>
            <div className="dc-production-url-block">
              <span className="dc-production-field-label">Website URL</span>
              <a
                className="dc-production-url"
                href={surfaceUrl}
                target="_blank"
                rel="noreferrer"
                title={surfaceUrl}
              >
                <i className="wb webconnect-box-arrow-in-up-right" aria-hidden="true"></i>
                <span>{surfaceUrl}</span>
              </a>
              {env.envType === PRODUCTION && urlToOpen ? (
                <a
                  className="dc-production-url"
                  href={urlToOpen}
                  target="_blank"
                  rel="noreferrer"
                  title={urlToOpen}
                >
                  <i className="wb webconnect-box-arrow-up-right" aria-hidden="true"></i>
                  <span>{urlToOpen}</span>
                </a>
              ) : null}
            </div>
            <label className="dc-production-field">
              <span className="dc-production-field-label">Version</span>
              <select
                className="dc-production-select"
                value={selVersion[env.envType]}
                onChange={(event) => handleVersionChange(event, env.envType)}
              >
                <option value="">Select Version</option>
                {versions.map((item) => (
                  <option value={item.currentVersion} key={item.currentVersion}>
                    v{item.currentVersion}
                  </option>
                ))}
              </select>
            </label>
            <div className="dc-production-option">
              <div>
                <div className="dc-production-option-label">Debug</div>
                <div className="dc-production-option-help">
                  Display logs in the browser console.
                </div>
              </div>
              <ToggleButton
                name="enableDebugMode"
                bgColor="drapcode-violet"
                onChange={() => handleDebugModeToggle(env.envType)}
                isChecked={
                  debugModeState[env.envType] ? debugModeState[env.envType].isEnable : false
                }
              />
            </div>

            <div className="dc-production-actions">
              <button
                type="button"
                className="dc-production-btn dc-production-btn-secondary"
                onClick={() => {
                  window.open(surfaceUrl);
                }}
              >
                <i className="wb webconnect-eye-fill" aria-hidden="true"></i>
                View
              </button>
              <button
                type="button"
                className="dc-production-btn dc-production-btn-primary"
                onClick={() => publishApp(env.envType, env.name, cleanPublishState[env.envType])}
                disabled={isPublishing}
              >
                <i className="wb webconnect-paper-plane-fill" aria-hidden="true"></i>
                Publish
                <CustomLoader loading={isPublishing} />
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );

  if (variant === 'panel' && embedded) {
    return productionEnvList;
  }

  if (variant === 'panel') {
    return (
      <div
        id={panelId}
        className="dc-production-panel"
        role="dialog"
        aria-modal="false"
        aria-label="Production"
        tabIndex={-1}
      >
        <div className="dc-production-panel-header">
          <div className="dc-production-panel-intro">
            <div className="dc-production-panel-heading">
              <span className="dc-production-dot" aria-hidden="true" />
              <span>Production</span>
            </div>
            <p className="dc-production-panel-subtitle">Publish your app for the end users.</p>
          </div>
          <Link
            to={{
              pathname: `/deployment-setting/${version}/v/${projectId}/code-export`,
            }}
            className="dc-production-btn dc-production-btn-secondary"
            title="Code Export"
            onClick={onExportCode}
          >
            <i className="wb webconnect-cursor-fill" aria-hidden="true"></i>
            Export Code
          </Link>
        </div>
        {productionEnvList}
      </div>
    );
  }

  return (
    <div className="row pt-2 pb-2">
      <div className="col-md-12 col-sm-12">
        <strong>Publish your app for the end users on specific environment.</strong>
        <table className="table table-stripped table-bordered table-hover table-sm">
          <thead>
            <tr>
              <th>Server</th>
              <th>URL</th>
              <th>Versions</th>
              <th>Debug</th>
              <th>Clean Publish</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {environments.map((env) => (
              <tr key={env.uuid}>
                <td>{snakeCaseToTitleCase(env.envType)}</td>
                <td style={{ wordBreak: 'break-all' }}>
                  <p>{generateProjectURL(env.envType, seoName)}</p>
                  {env.envType === PRODUCTION && urlToOpen && (
                    <p>
                      <i
                        className="wb webconnect-box-arrow-up-right"
                        title="Open link in new tab"
                      ></i>
                      <a
                        className="icon-link"
                        style={{ color: '#1F2041' }}
                        href={urlToOpen}
                        target="_blank"
                        rel="noreferrer"
                        title="Open link in new tab"
                      >
                        {voca.truncate(urlToOpen, 50)}
                      </a>
                    </p>
                  )}
                </td>
                <td>
                  <select
                    className="form-control"
                    value={selVersion[env.envType]}
                    onChange={(event) => handleVersionChange(event, env.envType)}
                  >
                    <option value="">Select Version</option>
                    {versions.map((item) => (
                      <option value={item.currentVersion} key={item.currentVersion}>
                        v{item.currentVersion}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <ToggleButton
                    name="enableDebugMode"
                    bgColor="drapcode-violet"
                    onChange={() => handleDebugModeToggle(env.envType)}
                    isChecked={
                      debugModeState[env.envType] ? debugModeState[env.envType].isEnable : false
                    }
                  />
                </td>
                <td>
                  <ToggleButton
                    name="cleanPublish"
                    bgColor="drapcode-violet"
                    onChange={() => handleCleanToggle(env.envType)}
                    isChecked={cleanPublishState[env.envType]}
                  />
                </td>
                <td>
                  <div className="d-flex justify-content-center my-3">
                    <button
                      type="button"
                      className="btn btn-theme-submit btn-sm"
                      onClick={() => {
                        window.open(generateProjectURL(env.envType, seoName, true));
                      }}
                    >
                      <i className="wb webconnect-eye-fill"></i>&nbsp;View
                    </button>
                    &nbsp;
                    <button
                      type="button"
                      className="btn btn-theme-submit btn-sm"
                      onClick={() =>
                        publishApp(env.envType, env.name, cleanPublishState[env.envType])
                      }
                      disabled={isPublishing}
                    >
                      <i className="wb webconnect-paper-plane-fill"></i>&nbsp;Publish
                      <CustomLoader loading={isPublishing} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductionPublishContent;
